import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { ClipboardList, Search, Plus, Calendar, RefreshCw, X } from 'lucide-react';
import api from '../../api';
import { toast } from 'sonner';

const MedicationLogPage = () => {
  const [logs, setLogs] = useState([]);
  const [medications, setMedications] = useState([]);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    patientId: '',
    patientName: '',
    medicationName: '',
    scheduledTime: '',
    givenTime: '',
    givenBy: '',
    status: 'Scheduled',
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, [selectedDate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [logsRes, medsRes, patientsRes] = await Promise.all([
        api.get(`/medication-log?date=${selectedDate}`),
        api.get('/medications'),
        api.get('/patients')
      ]);
      
      if (logsRes.data.success) setLogs(logsRes.data.logs || []);
      if (medsRes.data.success) setMedications(medsRes.data.medications || []);
      if (patientsRes.data.success) setPatients(patientsRes.data.patients || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load medication log');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.patientName || !formData.medicationName) {
      toast.error('Please fill in patient and medication');
      return;
    }
    try {
      const res = await api.post('/medication-log', {
        ...formData,
        date: selectedDate
      });
      if (res.data.success) {
        toast.success('Medication administration recorded');
        setLogs(prev => [...prev, res.data.log]);
        setShowModal(false);
        setFormData({
          patientId: '', patientName: '', medicationName: '',
          scheduledTime: '', givenTime: '', givenBy: '',
          status: 'Scheduled', notes: ''
        });
      }
    } catch (error) {
      toast.error('Failed to record administration');
    }
  };

  const updateStatus = async (logId, newStatus) => {
    try {
      const givenTime = newStatus === 'Given' ? new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : '';
      await api.put(`/medication-log/${logId}`, { status: newStatus, givenTime });
      setLogs(prev => prev.map(l => l.id === logId ? { ...l, status: newStatus, givenTime } : l));
      toast.success(`Status updated to ${newStatus}`);
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const filteredLogs = logs.filter(entry =>
    entry.patientName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    entry.medicationName?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const stats = {
    given: filteredLogs.filter(l => l.status === 'Given').length,
    pending: filteredLogs.filter(l => l.status === 'Pending').length,
    scheduled: filteredLogs.filter(l => l.status === 'Scheduled').length,
    missed: filteredLogs.filter(l => l.status === 'Missed').length
  };

  return (
    <DashboardLayout>
      <div className="p-6" data-testid="medication-log-page">
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <ClipboardList className="w-6 h-6 text-[#f39c12]" />
                Medication Administration Log
              </h1>
              <p className="text-gray-500 text-sm mt-1">Track medication administration times and compliance</p>
            </div>
            <div className="flex gap-2">
              <Button onClick={fetchData} variant="outline">
                <RefreshCw className="w-4 h-4 mr-2" />Refresh
              </Button>
              <Button className="bg-[#f39c12] hover:bg-[#e67e22] text-white" onClick={() => setShowModal(true)}>
                <Plus className="w-4 h-4 mr-2" />Record Administration
              </Button>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="text-2xl font-bold text-green-700">{stats.given}</div>
            <div className="text-sm text-green-600">Given</div>
          </div>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="text-2xl font-bold text-yellow-700">{stats.pending}</div>
            <div className="text-sm text-yellow-600">Pending</div>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="text-2xl font-bold text-blue-700">{stats.scheduled}</div>
            <div className="text-sm text-blue-600">Scheduled</div>
          </div>
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="text-2xl font-bold text-red-700">{stats.missed}</div>
            <div className="text-sm text-red-600">Missed</div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
          <div className="flex items-center gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input placeholder="Search by patient or medication..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10" />
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-gray-400" />
              <Input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} className="w-40" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          ) : filteredLogs.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No medication logs for this date. Click "Record Administration" to add one.</div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Patient</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Medication</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Scheduled</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Given</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Given By</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredLogs.map((entry) => (
                  <tr key={entry.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-medium">{entry.patientName}</td>
                    <td className="px-6 py-4">{entry.medicationName}</td>
                    <td className="px-6 py-4">{entry.scheduledTime || '-'}</td>
                    <td className="px-6 py-4">{entry.givenTime || '-'}</td>
                    <td className="px-6 py-4">{entry.givenBy || '-'}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        entry.status === 'Given' ? 'bg-green-100 text-green-800' :
                        entry.status === 'Pending' ? 'bg-yellow-100 text-yellow-800' :
                        entry.status === 'Missed' ? 'bg-red-100 text-red-800' :
                        'bg-blue-100 text-blue-800'
                      }`}>
                        {entry.status}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {entry.status !== 'Given' && (
                        <Button size="sm" variant="outline" onClick={() => updateStatus(entry.id, 'Given')}>
                          Mark Given
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold">Record Medication Administration</h2>
                <button onClick={() => setShowModal(false)}><X className="w-5 h-5" /></button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Patient *</label>
                  <select 
                    value={formData.patientId}
                    onChange={(e) => {
                      const p = patients.find(p => p.id === e.target.value);
                      setFormData(prev => ({ ...prev, patientId: e.target.value, patientName: p ? `${p.firstName} ${p.lastName}` : '' }));
                    }}
                    className="w-full border rounded-md px-3 py-2"
                  >
                    <option value="">Select Patient</option>
                    {patients.map(p => <option key={p.id} value={p.id}>{p.firstName} {p.lastName}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Medication *</label>
                  <Input value={formData.medicationName} onChange={(e) => setFormData(prev => ({ ...prev, medicationName: e.target.value }))} placeholder="e.g., Metformin 500mg" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Scheduled Time</label>
                    <Input type="time" value={formData.scheduledTime} onChange={(e) => setFormData(prev => ({ ...prev, scheduledTime: e.target.value }))} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Status</label>
                    <select value={formData.status} onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))} className="w-full border rounded-md px-3 py-2">
                      <option value="Scheduled">Scheduled</option>
                      <option value="Pending">Pending</option>
                      <option value="Given">Given</option>
                      <option value="Missed">Missed</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Given By</label>
                  <Input value={formData.givenBy} onChange={(e) => setFormData(prev => ({ ...prev, givenBy: e.target.value }))} placeholder="Nurse name" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Notes</label>
                  <textarea value={formData.notes} onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))} className="w-full border rounded-md px-3 py-2" rows={2} />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
                <Button className="bg-[#f39c12] hover:bg-[#e67e22] text-white" onClick={handleSubmit}>Save</Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default MedicationLogPage;
